import { useCallback, useLayoutEffect, useRef, type ForwardedRef, type KeyboardEvent, type RefCallback } from "react";

const NEXT = new Set(["ArrowDown", "ArrowRight"]);
const PREVIOUS = new Set(["ArrowUp", "ArrowLeft"]);
const RADIO_ITEMS = '[role="radio"], input[type="radio"]';

/** Options of `useRovingFocus`: `selector` finds the enabled items, `onMove` selects the newly focused one. */
export type RovingFocusOptions<T extends HTMLElement> = {
  selector: string;
  onMove?: (item: T) => void;
};

/**
 * Keydown handler for a radiogroup-like container: arrows move (wrapping), Home/End jump, focus follows selection.
 * `onMove` fires only when focus actually lands on another item, so a lone option or Home on the first never re-fires it.
 */
export function useRovingFocus<T extends HTMLElement>({ selector, onMove }: RovingFocusOptions<T>) {
  return useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const isHome = event.key === "Home";
      const isEnd = event.key === "End";
      const step = NEXT.has(event.key) ? 1 : PREVIOUS.has(event.key) ? -1 : 0;
      if (step === 0 && !isHome && !isEnd) return;
      const items = Array.from(event.currentTarget.querySelectorAll<T>(selector));
      if (items.length === 0) return;
      event.preventDefault();
      const current = items.findIndex((item) => item === document.activeElement);
      const index = isHome ? 0 : isEnd ? items.length - 1 : (current + step + items.length) % items.length;
      const next = items[index];
      if (next === document.activeElement) return;
      next.focus();
      onMove?.(next);
    },
    [selector, onMove],
  );
}

function isChecked(item: HTMLElement): boolean {
  return item.getAttribute("aria-checked") === "true" || (item instanceof HTMLInputElement && item.checked);
}

function isDisabled(item: HTMLElement): boolean {
  return (item as HTMLButtonElement | HTMLInputElement).disabled === true;
}

function assignTabStop(container: HTMLElement) {
  const items = Array.from(container.querySelectorAll<HTMLElement>(RADIO_ITEMS)).filter(
    (item) => item.closest('[role="radiogroup"]') === container,
  );
  const enabled = items.filter((item) => !isDisabled(item));
  const stop = enabled.find(isChecked) ?? enabled[0];
  for (const item of items) item.tabIndex = item === stop ? 0 : -1;
}

/**
 * Keeps exactly one radio of the group in the tab order: the checked one, or the first enabled one when the group
 * value matches no option (stale value) or is empty. Returns the ref for the `role="radiogroup"` container.
 */
export function useRovingTabStop<T extends HTMLElement>(forwardedRef?: ForwardedRef<T>): RefCallback<T> {
  const node = useRef<T | null>(null);

  useLayoutEffect(() => {
    if (node.current) assignTabStop(node.current);
  });

  return useCallback(
    (element: T | null) => {
      node.current = element;
      if (element) assignTabStop(element);
      if (typeof forwardedRef === "function") forwardedRef(element);
      else if (forwardedRef) forwardedRef.current = element;
    },
    [forwardedRef],
  );
}
